import { LeadRelationshipActiveCampaign } from "@signalist/shared-types";

import { AssignStatus } from "@/enum/list.enum";

import { ListPerson } from "@/pages/Private/pages/List/schema/list";

import { LeadLocation, resolveLeadLocation } from "../../utils/resolveLeadLocation";

import { isLeadInCampaign } from "./leadPanelActions.utils";
import { resolveOutreachHoldState } from "./outreachHoldState";

/** Only these two destinations have a meaningful per-lead deep link. */
const isDeepLinkable = (location: LeadLocation): boolean =>
	location.kind === "INBOX" || location.kind === "OUTBOX";

export type CampaignActionsPlan =
	/** Nothing to hold and nothing to release — the ordinary reach-out + "Add to" row. */
	| { kind: "default" }
	/** The lead has campaign work queued on *this* row: the primary slot becomes "Pause outreach". */
	| { kind: "campaign"; pauseListPersonId: number }
	/**
	 * The campaign is running on a **different** list. The lead is a normal lead *here*, so it keeps
	 * the ordinary row and gains a notice pointing at where the outreach actually lives.
	 */
	| { kind: "elsewhere"; campaignName: string; listName: string; to: string }
	/**
	 * This row's outreach is held. Keeps the ordinary row — the lead can still be contacted by hand —
	 * and offers to put the campaign back in motion.
	 */
	| { kind: "paused"; resumeListPersonId: number };

const DEFAULT: CampaignActionsPlan = { kind: "default" };

/**
 * Whether the panel has to ask the relationship endpoint at all.
 *
 * `row.labels` is computed server-side and is already prospect-wide — the very thing the panel is
 * missing — so a row that carries labels and is *not* flagged `IN_ACTIVE_CAMPAIGN` settles the
 * question for free, and the request is skipped. Surfaces whose row has no labels (the Contacts
 * panel and the extension both resolve their `ListPerson` from a detail endpoint that does not
 * attach them) still have to ask.
 */
export const needsCampaignLookup = (
	listPerson: ListPerson | null | undefined,
	enabled: boolean
): boolean => {
	if (!listPerson?.id || !enabled) {
		return false;
	}

	return listPerson.labels ? listPerson.labels.includes("IN_ACTIVE_CAMPAIGN") : true;
};

export interface CampaignActionsInput {
	/** The row the panel was opened on. */
	listPerson?: ListPerson | null;
	/**
	 * The prospect-wide active campaign from `GET /list_person/:id/relationships`. `undefined` while
	 * it loads or when the request failed — the plan then falls back to what the row alone knows.
	 */
	activeCampaign?: LeadRelationshipActiveCampaign | null;
	/** Used to pre-filter the Inbox/Outbox deep link, which has no per-lead route. */
	prospectName?: string | null;
	/** `false` on surfaces that opt out of campaign actions entirely. */
	enabled: boolean;
	now?: Date;
}

/**
 * Decide what the lead-panel action row does about the lead's campaign.
 *
 * The bug this exists for (PREC-3906): the red `IN_ACTIVE_CAMPAIGN` dot is computed **prospect-wide**
 * — any list — while the panel only ever held the single row it was opened on. A lead sitting in the
 * New tab of one list (always `DRAFT` there, so never pausable) while being emailed from another
 * showed a red dot and a "Reach out" button, with no way to see or stop the outreach. Reading the
 * relationship payload lets the panel tell the situations apart instead of guessing from the row.
 *
 * Precedence, most urgent first: a campaign running somewhere else beats this row's own state — a
 * lead paused here while another list is actively mailing them needs pointing at that list, not a
 * resume button for a campaign that is already the quieter of the two. Everything after that is
 * {@link resolveOutreachHoldState}, so what the row *says* and what the button *does* are one
 * decision.
 */
export const resolveCampaignActions = ({
	listPerson,
	activeCampaign,
	prospectName,
	enabled,
	now = new Date(),
}: CampaignActionsInput): CampaignActionsPlan => {
	if (!enabled) {
		return DEFAULT;
	}

	if (activeCampaign && activeCampaign.listPersonId !== listPerson?.id) {
		return {
			kind: "elsewhere",
			campaignName: activeCampaign.campaignName,
			listName: activeCampaign.listName,
			to: resolveLeadLocation({
				assignStatus: activeCampaign.assignStatus as AssignStatus,
				listId: activeCampaign.listId,
				listName: activeCampaign.listName,
				prospectName,
			}).to,
		};
	}

	if (!listPerson) {
		return DEFAULT;
	}

	const holdState = resolveOutreachHoldState(listPerson, now);

	if (holdState === "resumable") {
		return { kind: "paused", resumeListPersonId: listPerson.id };
	}

	if (holdState === "none") {
		return DEFAULT;
	}

	return { kind: "campaign", pauseListPersonId: listPerson.id };
};

/**
 * Where this lead currently lives, as a link — offered beside the primary action for a lead whose
 * campaign runs on this row, and only where the surface is not already *that* place (the Messages
 * rails pass `locationLinkEnabled: false`).
 *
 * Deliberately its own decision rather than a field on the plan: "what may I do about the outreach?"
 * and "where does this lead live?" are separate questions, and answering them in one branch is how
 * a lead whose sequence had ended lost its Outbox link the moment the pause stopped applying.
 */
export const resolveCampaignLocationLink = ({
	plan,
	listPerson,
	enabled,
	locationLinkEnabled = true,
}: {
	plan: CampaignActionsPlan;
	listPerson?: ListPerson | null;
	enabled: boolean;
	locationLinkEnabled?: boolean;
}): LeadLocation | null => {
	const applies =
		plan.kind === "campaign" || (plan.kind === "default" && isLeadInCampaign(listPerson));

	if (!enabled || !locationLinkEnabled || !listPerson || !applies) {
		return null;
	}

	const location = resolveLeadLocation({
		assignStatus: listPerson.assignStatus,
		listId: listPerson.list?.id,
		listName: listPerson.list?.name,
		prospectName: listPerson.prospect?.name,
	});

	return isDeepLinkable(location) ? location : null;
};
